import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '@material-ui/core';
import { useTranslation } from 'next-i18next';

export const MARGIN = 50;
export const SVG_HEIGHT = 900;
export const SVG_WIDTH = 1600;

export interface TimeFrameType {
  i18nKey: string;
  minDataLength: number;
  timeFrameLength: number;
  requiredIntervalDesktop: 'daily' | 'weekly' | 'monthly';
  requiredIntervalMobile: 'daily' | 'weekly' | 'monthly';
}
export interface ControlStateType {
  selectedChartType: 'ACCUMULATIVE' | 'DELTA';
  selectedTimeFrame: TimeFrameType;
}
export interface RawChartDataType {
  chartLength: number;
  lastTimestamp: number;
  testingTheWaters: FullChartData;
  live: FullChartData;
}

type ChartData = {
  investors: [number, number][];
  commitments: [number, number][];
};
type FullChartData = {
  delta: {
    daily: ChartData;
    weekly: ChartData;
    monthly: ChartData;
  };
  accumulative: ChartData;
};
type DataType = [Date, number][];
type DataInputType = [number, number][];

function parseDates(input: DataInputType, minTimestamp: number): DataType {
  return input
    .filter((x) => x[0] >= minTimestamp)
    .map((values) => [new Date(values[0]), values[1]]);
}

const formatDate = (format: string, date: Date, i18n: string) => {
  switch (format) {
    case '1W':
      return date.toLocaleDateString(i18n, { weekday: 'short' });
    case '1M':
      if (date.toLocaleDateString(i18n, { day: 'numeric' }) === '1') return date.toLocaleDateString(i18n, { day: 'numeric', month: 'short' });
      return date.toLocaleDateString(i18n, { day: 'numeric' });
    case '3M':
      if (date.toLocaleDateString(i18n, { day: 'numeric' }) === '1') return date.toLocaleDateString(i18n, { day: 'numeric', month: 'short' });
      return date.toLocaleDateString(i18n, { day: 'numeric' });
    case '1Y':
      return date.toLocaleDateString(i18n, { month: 'short', year: 'numeric' });
    default:
      return date.toLocaleDateString(i18n, { month: 'short' });
  }
};

const useDerivedChartData = (
  chartData: RawChartDataType,
  controlState: ControlStateType,
  isMobile: boolean,
) => {
  const minTimestamp = chartData.lastTimestamp - controlState.selectedTimeFrame.timeFrameLength;
  if (isMobile) {
    return {
      live: {
        deltaCommitments: parseDates(
          chartData.live.delta[
            controlState.selectedTimeFrame.requiredIntervalMobile
          ].commitments,
          minTimestamp,
        ),
        deltaInvestors: parseDates(
          chartData.live.delta[
            controlState.selectedTimeFrame.requiredIntervalMobile
          ].investors,
          minTimestamp,
        ),
        accumulativeCommitments: parseDates(
          chartData.live.accumulative.commitments,
          minTimestamp,
        ),
      },
      testingTheWaters: {
        deltaCommitments: parseDates(
          chartData.testingTheWaters.delta[
            controlState.selectedTimeFrame.requiredIntervalMobile
          ].commitments,
          minTimestamp,
        ),
        deltaInvestors: parseDates(
          chartData.testingTheWaters.delta[
            controlState.selectedTimeFrame.requiredIntervalMobile
          ].investors,
          minTimestamp,
        ),
        accumulativeCommitments: parseDates(
          chartData.testingTheWaters.accumulative.commitments,
          minTimestamp,
        ),
      },
    };
  }
  return {
    live: {
      deltaCommitments: parseDates(
        chartData.live.delta[
          controlState.selectedTimeFrame.requiredIntervalDesktop
        ].commitments,
        minTimestamp,
      ),
      deltaInvestors: parseDates(
        chartData.live.delta[
          controlState.selectedTimeFrame.requiredIntervalDesktop
        ].investors,
        minTimestamp,
      ),
      accumulativeCommitments: parseDates(
        chartData.live.accumulative.commitments,
        minTimestamp,
      ),
    },
    testingTheWaters: {
      deltaCommitments: parseDates(
        chartData.testingTheWaters.delta[
          controlState.selectedTimeFrame.requiredIntervalDesktop
        ].commitments,
        minTimestamp,
      ),
      deltaInvestors: parseDates(
        chartData.testingTheWaters.delta[
          controlState.selectedTimeFrame.requiredIntervalDesktop
        ].investors,
        minTimestamp,
      ),
      accumulativeCommitments: parseDates(
        chartData.testingTheWaters.accumulative.commitments,
        minTimestamp,
      ),
    },
  };
};

/* function scaleBandInvert(scale) {
  const domain = scale.domain();
  const paddingOuter = scale(domain[0]);
  const eachBand = scale.step();
  return function (value) {
    const index = Math.floor(((value - paddingOuter) / eachBand));
    return domain[Math.max(0, Math.min(index, domain.length - 1))];
  };
} */

const InfinityToZero = (value: number) => (Number.isFinite(value) ? value : 0.0);

const useChart = (
  ref: React.MutableRefObject<SVGSVGElement | null>,
  data: RawChartDataType,
  controlState: ControlStateType,
  isMobile: boolean,
) => {
  const theme = useTheme();
  const { live, testingTheWaters } = useDerivedChartData(
    data,
    controlState,
    isMobile,
  );
  const [currentSelection, setCurrentSelection] = useState({
    x: new Date(),
    raised: 30000,
    deltaInvestors: 200,
    deltaCommitments: 24000,
  });

  // ACCUMULATIVE
  const accumulativeDomain = [
    ...live.accumulativeCommitments,
    ...testingTheWaters.accumulativeCommitments,
  ];
  const curve = d3.curveLinear;
  const accumulativeX = d3
    .scaleUtc()
    .domain(d3.extent(accumulativeDomain, (d) => d[0]) as Iterable<Date>)
    .range([MARGIN, SVG_WIDTH]);
  const accumulativeY = d3
    .scaleLinear()
    .domain([0, d3.max(accumulativeDomain, (d) => d[1]) as number])
    .nice()
    .range([SVG_HEIGHT - MARGIN, MARGIN]);
  const line = d3
    .line<[Date, number]>()
    .defined((d) => !Number.isNaN(d[1]) || !d[1])
    .x((d) => accumulativeX(d[0]))
    .y((d) => accumulativeY(d[1]));
  const area = d3
    .area<[Date, number]>()
    .curve(curve)
    .x((d) => accumulativeX(d[0]))
    .y0(accumulativeY(0))
    .y1((d) => accumulativeY(d[1]));

  // delta investors
  const deltaInvestorsDomain = [
    ...live.deltaInvestors,
    ...testingTheWaters.deltaInvestors,
  ].sort((a, b) => a[0] - b[0]);

  const deltaCommitmentsDomain = [
    ...live.deltaCommitments,
    ...testingTheWaters.deltaCommitments,
  ];

  const deltas = deltaInvestorsDomain.map((d) => {
    const findCom = deltaCommitmentsDomain.find(
      (c) => c[0].getTime() === d[0].getTime(),
    );
    return {
      timestamp: d[0],
      investors: d[1],
      commitments: findCom ? findCom[1] : 0,
    };
  });
  const subgroups = ['deltaCommitments', 'deltaInvestors'];
  const deltaInvestorsX = d3
    .scaleBand()
    .domain(
      d3.map(deltaInvestorsDomain, (d) => d[0].toLocaleDateString('es')) as Iterable<string>,
    )
    .padding(deltas.length > 10 ? 0.5 : 0.7)
    .range([MARGIN, SVG_WIDTH]);
  deltaInvestorsX.invert = function (x) {
    const domain = this.domain();
    const range = this.range();
    const scale = d3.scaleQuantize().domain(range).range(domain);
    return scale(x);
  };
  // deltaInvestorsX.invert = function (_) {
  //   const scale = this;
  //   const { domain } = scale;
  //   const paddingOuter = scale.paddingOuter();
  //   const paddingInner = scale.paddingInner();
  //   const step = scale.step();

  //   const range = scale.range();
  //   let domainIndex;
  //   const n = domain().length;
  //   const reverse = range[1] < range[0];
  //   const start = range[reverse - 0];
  //   const stop = range[1 - reverse];

  //   if (_ < start + paddingOuter * step) domainIndex = 0;
  //   else if (_ > stop - paddingOuter * step) domainIndex = n - 1;
  //   else domainIndex = Math.floor((_ - start - paddingOuter * step) / step);

  //   return domain()[domainIndex];
  // };
  const xSubgroup = d3
    .scaleBand()
    .domain(subgroups)
    .range([0, deltaInvestorsX.bandwidth()])
    .padding(0);

  // calculate Y domains
  // we have to figure out who has a higher pct of negative values
  // so that we can set the other domain to have the same ratio pos/neg -->
  // zero line will be in the same place

  const posRanges = {
    investors: Math.max(
      ...deltaInvestorsDomain.map((d) => d[1]).filter((d) => d > 0),
    ),
    commitments: Math.max(
      ...deltaCommitmentsDomain.map((d) => d[1]).filter((d) => d > 0),
    ),
  };
  const negRanges = {
    investors: InfinityToZero(Math.min(
      ...deltaInvestorsDomain.map((d) => d[1]).filter((d) => d < 0),
    )),
    commitments: InfinityToZero(Math.min(
      ...deltaCommitmentsDomain.map((d) => d[1]).filter((d) => d < 0),
    )),
  };

  const posNegPct = {
    investors:
      Math.max(0, posRanges.investors)
      / Math.abs(Math.min(1, negRanges.investors)),
    commitments:
      Math.max(0, posRanges.commitments)
      / Math.abs(Math.min(1, negRanges.commitments)),
  };
  const adjustedDeltaCommitmentsDomain = [
    posNegPct.commitments < posNegPct.investors
      ? Math.min(0, negRanges.commitments)
      : -((1 / posNegPct.investors) * posRanges.commitments),
    d3.max(deltaCommitmentsDomain, (d) => d[1]) as number,
  ];
  const adjustedDeltaInvestorsDomain = [
    posNegPct.investors < posNegPct.commitments
      ? Math.min(0, negRanges.investors)
      : -((1 / posNegPct.commitments) * posRanges.investors),
    d3.max(deltaInvestorsDomain, (d) => d[1]) as number,
  ];
  // console.log('posRanges', posRanges);
  // console.log('negRanges', negRanges);
  // console.log('posNegPct', posNegPct);
  // console.log('adjustedDeltaInvestorsDomain', adjustedDeltaInvestorsDomain);
  // console.log('adjustedDeltaCommitmentsDomain', adjustedDeltaCommitmentsDomain);
  const deltaCommitmentsY = d3
    .scaleLinear()
    .domain(adjustedDeltaCommitmentsDomain)
    .range([SVG_HEIGHT - MARGIN, MARGIN]);

  const deltaInvestorsY = d3
    .scaleLinear()
    .domain(adjustedDeltaInvestorsDomain)
    .range([SVG_HEIGHT - MARGIN, MARGIN]);

  const getYForBarChart = (
    key: 'deltaInvestors' | 'deltaCommitments',
    value: number,
  ) => (key === 'deltaInvestors'
    ? deltaInvestorsY(value)
    : deltaCommitmentsY(value));

  const { i18n } = useTranslation();

  useEffect(() => {
    if (ref.current) {
      const svgElement = d3.select(ref.current);
      // reset svg element
      svgElement.selectAll('*').remove();

      if (controlState.selectedChartType === 'ACCUMULATIVE') {
        // GRADIENT
        // =================================================================

        // gradient accumulative
        // =================================================================
        const gradient = svgElement
          .append('defs')
          .append('linearGradient')
          .attr('id', 'mygrad') // id of the gradient
          .attr('x1', '0%')
          .attr('x2', '0%')
          .attr('y1', '0%')
          .attr('y2', '100%');
        gradient
          .append('stop')
          .attr('offset', '0%')
          .style('stop-color', theme.palette.primary.main)
          .style('stop-opacity', 0.8);
        gradient
          .append('stop')
          .attr('offset', '100%')
          .style('stop-color', theme.palette.primary.main)
          .style('stop-opacity', 0);
        svgElement
          .append('path')
          .datum(live.accumulativeCommitments)
          .attr('fill', 'url(#mygrad)')
          .attr('fill-opacity', 0.3)
          .attr('d', area);

        // gradient testing the waters
        // =================================================================
        const gradientTTW = svgElement
          .append('defs')
          .append('linearGradient')
          .attr('id', 'gradientTTW') // id of the gradient
          .attr('x1', '0%')
          .attr('x2', '0%')
          .attr('y1', '0%')
          .attr('y2', '100%');
        gradientTTW
          .append('stop')
          .attr('offset', '0%')
          .style('stop-color', '#7F818F')
          .style('stop-opacity', 0.8);
        gradientTTW
          .append('stop')
          .attr('offset', '100%')
          .style('stop-color', '#D4D5DA')
          .style('stop-opacity', 0.2);
        svgElement
          .append('path')
          .datum(testingTheWaters.accumulativeCommitments)
          .attr('fill', 'url(#gradientTTW)')
          .attr('fill-opacity', 0.3)
          .attr('d', area);

        // line chart
        // =================================================================
        svgElement
          .append('path')
          .datum(live.accumulativeCommitments)
          .attr('fill', 'none')
          .attr('stroke', theme.palette.primary.main)
          .attr('stroke-width', 4)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('d', line);
        svgElement
          .append('path')
          .datum(testingTheWaters.accumulativeCommitments)
          .attr('fill', 'none')
          .attr('stroke', theme.palette.greyScale.dark)
          .attr('stroke-width', 4)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('d', line);

        // AXIS
        // =================================================================

        // X axis
        // =================================================================
        if (!isMobile && controlState.selectedTimeFrame.i18nKey !== 'ALL') {
          svgElement
            .append('g')
            .style('font-size', '20px')
            .style('font-family', 'Ubuntu')
            .attr('transform', `translate(0, ${SVG_HEIGHT - MARGIN})`)
            .call(
              d3.axisBottom(accumulativeX).tickFormat((value) => {
                const date = formatDate(
                  controlState.selectedTimeFrame.i18nKey,
                  new Date(value),
                  i18n.language,
                );
                return date;
              }),
            )
            .call((g) => g.select('.domain').remove())
            .call((g) => g.selectAll('text').attr('fill', theme.palette.greyScale.dark));
        }

        // Y axis
        // =================================================================
        svgElement
          .append('g')
          .style('font-size', '20px')
          .style('font-family', 'Ubuntu')
          .attr('transform', `translate(${MARGIN}, 0)`)
          .call(d3
            .axisLeft(accumulativeY)
            .tickFormat(d3.format('.2s')))
          .call((g) => g.select('.domain').remove())
          .call((g) => g.selectAll('text').attr('fill', theme.palette.greyScale.dark));

        // grid lines
        // =================================================================
        if (!isMobile) {
          svgElement
            .append('g')
            .attr('class', 'x axis')
            .attr('transform', `translate(${MARGIN}, 0)`)
            .style('stroke-dasharray', '10, 5')
            .call(
              d3
                .axisLeft(accumulativeY)
                .tickFormat('')
                .tickSize(-(SVG_WIDTH - MARGIN)),
            )
            .call((g) => g.select('.domain').remove());
          d3.selectAll('.x.axis line')
            .style('stroke', theme.palette.greyScale.light)
            .attr('x2', SVG_WIDTH - MARGIN * 2);

          d3.selectAll('.x.axis line')
            .style('stroke', '#dedede')
            .attr('x2', SVG_WIDTH - MARGIN);
        }

        // CHANGE DATE ON-MOUSE-MOVE
        // =================================================================
        const crossHairRect = svgElement
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', SVG_WIDTH)
          .attr('height', SVG_HEIGHT)
          .attr('opacity', 0);

        crossHairRect.on('mousemove', (d) => {
          const coords = d3.pointer(d);
          const xGoal = accumulativeX.invert(coords[0] - MARGIN);
          const currX = accumulativeDomain
            .map((item) => item[0])
            .reduce(
              (prev, curr) => (Math.abs(curr - xGoal) < Math.abs(prev - xGoal) ? curr : prev),
            );
          const currY = accumulativeDomain.find((item) => item[0] === currX)[1];
          setCurrentSelection({
            x: currX,
            raised: currY,
            deltaInvestors: 20,
            deltaCommitments: 24000,
          });
        });
      } else {
        // DELTA CHART_DATA
        // =================================================================

        // gradient testing the waters
        // =================================================================
        const gradientTTW = svgElement
          .append('defs')
          .append('linearGradient')
          .attr('id', 'ttwGradient')
          .attr('gradientTransform', 'rotate(90)');
        gradientTTW
          .append('stop')
          .attr('stop-color', '#A9ABB5')
          .style('stop-opacity', 0.15)
          .attr('offset', '0%');
        gradientTTW
          .append('stop')
          .attr('stop-color', '#D4D5DA')
          .style('stop-opacity', 0.25)
          .attr('offset', '100%');

        // testing the waters rect
        // =================================================================
        const xRect = testingTheWaters.deltaCommitments.length > 0
          ? deltaInvestorsX(
            testingTheWaters.deltaCommitments[0][0].toLocaleDateString('es'),
          ) : 0;
        const widthRect = testingTheWaters.deltaCommitments.length > 0
          ? deltaInvestorsX(
            testingTheWaters.deltaCommitments[
              testingTheWaters.deltaCommitments.length - 1
            ][0].toLocaleDateString('es'),
          )
                  - deltaInvestorsX(
                    testingTheWaters.deltaCommitments[0][0].toLocaleDateString(
                      'es',
                    ),
                  )
          : 0;
        svgElement
          .append('g')
          .append('rect')
          .attr('x', xRect)
          .attr('y', MARGIN)
          .attr('width', widthRect)
          .attr('height', SVG_HEIGHT - MARGIN * 2)
          .attr('fill', 'url(#ttwGradient)');

        // AXES
        // =================================================================

        // X axis
        // =================================================================
        if (!isMobile && controlState.selectedTimeFrame.i18nKey !== 'ALL') {
          svgElement
            .append('g')
            .style('font-size', '20px')
            .style('font-family', 'Ubuntu')
            .attr('transform', `translate(0, ${SVG_HEIGHT - MARGIN})`)
            .call(
              d3.axisBottom(deltaInvestorsX).tickFormat((value) => {
                const dateSplit = value.split('/');
                const newValue = new Date(`${dateSplit[1]}/${dateSplit[0]}/${dateSplit[2]}`);
                const date = formatDate(
                  controlState.selectedTimeFrame.i18nKey,
                  newValue,
                  i18n.language,
                );
                return date;
              }),
            )
            .call((g) => g.select('.domain').remove())
            .call((g) => g.selectAll('text').attr('fill', theme.palette.greyScale.dark));
        }

        // calculate tick values
        // [MIN, 0, MAX]
        // --> MIN_BOUND, MAX_BOUND
        // --> [MIN, MIN_BOUND, 0.5 * MIN_BOUND, 0, 0.25 * MAX_BOUND,
        // 0.5 * MAX_BOUND, 0.75 * MAX_BOUND, MAX_BOUND, MAX]

        // calc bounds

        // Y axis (right)
        // =================================================================
        svgElement
          .append('g')
          .style('font-size', '20px')
          .style('font-family', 'Ubuntu')
          .attr('transform', `translate(${SVG_WIDTH - MARGIN}, 0)`)

          .call(
            d3
              .axisRight(deltaInvestorsY)
              .tickFormat(d3.format(''))
              .tickSize(0)
              .tickValues([
                Math.round(adjustedDeltaInvestorsDomain[0]),
                0,
                0.5
                  * Math.round(
                    adjustedDeltaInvestorsDomain[
                      adjustedDeltaInvestorsDomain.length - 1
                    ],
                  ),
                posRanges.investors,
              ]),
          )
          .call((g) => g.select('.domain').remove())
          .call((g) => g.selectAll('text').attr('fill', theme.palette.greyScale.dark));

        // Y axis (left)
        // =================================================================
        svgElement
          .append('g')
          .style('font-size', '20px')
          .style('font-family', 'Ubuntu')
          .attr('transform', `translate(${MARGIN}, 0)`)
          .call(
            d3
              .axisLeft(deltaCommitmentsY)
              .tickFormat(d3.format('.2s'))
              .tickSize(0)
              .tickValues([
                Math.round(negRanges.commitments),
                0,
                posRanges.commitments,
              ]),
          )
          .call((g) => g.select('.domain').remove())
          .call((g) => g.selectAll('text').attr('fill', theme.palette.greyScale.dark));

        // horizontal gridlines
        // =================================================================
        svgElement
          .append('g')
          .attr('class', 'x axis')
          .attr('transform', `translate(${MARGIN}, 0)`)
          .style('stroke-dasharray', '10, 5')
          .call(
            d3
              .axisLeft(deltaCommitmentsY)
              .tickValues([
                Math.round(negRanges.commitments),
                0,
                0.5 * Math.round(posRanges.commitments),
                posRanges.commitments,
              ])
              .tickFormat(() => '')
              .tickSize(-(SVG_WIDTH - MARGIN)),
          )
          .call((g) => g.select('.domain').remove());

        d3.selectAll('.x.axis line')
          .style('stroke', theme.palette.greyScale.light)
          .attr('x2', SVG_WIDTH - MARGIN * 2);

        // GRADIENTS
        // =================================================================

        // gradient money raised
        // =================================================================
        const bgGradientRaised = svgElement
          .append('defs')
          .append('linearGradient')
          .attr('id', 'bgGradientRaised')
          .attr('gradientTransform', 'rotate(90)');
        bgGradientRaised
          .append('stop')
          .attr('stop-color', '#3F51B5')
          .attr('offset', '0%');
        bgGradientRaised
          .append('stop')
          .attr('stop-color', '#2F3D88')
          .attr('offset', '100%');

        // gradient investors
        // =================================================================
        const bgGradientInvestors = svgElement
          .append('defs')
          .append('linearGradient')
          .attr('id', 'bgGradientInvestors')
          .attr('gradientTransform', 'rotate(90)');
        bgGradientInvestors
          .append('stop')
          .attr('stop-color', '#FFDA6A')
          .attr('offset', '0%');
        bgGradientInvestors
          .append('stop')
          .attr('stop-color', '#FFC107')
          .attr('offset', '100%');

        // LEYENDA
        // =================================================================
        const crossHairRect = svgElement
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', SVG_WIDTH)
          .attr('height', SVG_HEIGHT)
          .attr('opacity', 0);

        crossHairRect.on('mousemove', (d) => {
          const coords = d3.pointer(d);
          const xGoal = deltaInvestorsX.invert(coords[0] - MARGIN);
          const dateSplit = xGoal.split('/');
          const currX = new Date(`${dateSplit[1]}/${dateSplit[0]}/${dateSplit[2]}`);
          const currDeltaInvestors = deltaInvestorsDomain.find(
            (item) => item[0].toLocaleDateString() === currX.toLocaleDateString(),
          )[1];
          const currDeltaCommitments = deltaCommitmentsDomain.find(
            (item) => item[0].toLocaleDateString() === currX.toLocaleDateString(),
          )[1];
          setCurrentSelection({
            x: currX,
            raised: 0,
            deltaInvestors: currDeltaInvestors,
            deltaCommitments: currDeltaCommitments,
          });
        });

        // BARS
        // =============================================
        svgElement
          .append('g')
          .selectAll('g')
          .data(deltas)
          .enter()
          .append('g')
          .attr(
            'transform',
            (d) => `translate(${deltaInvestorsX(
              d.timestamp.toLocaleDateString('es'),
            )},${0})`,
          )
          .selectAll('rect')
          .data((d) => subgroups.map((key) => (key === 'deltaInvestors'
            ? { key, value: d.investors }
            : {
              key,
              value: d.commitments,
            })))
          .enter()
          .append('rect')
          .attr('x', (d) => xSubgroup(d.key))
          .attr('width', xSubgroup.bandwidth())
          .attr('fill', (d) => (d.key === 'deltaCommitments'
            ? 'url(#bgGradientRaised)'
            : 'url(#bgGradientInvestors)'))
          .attr('y', (d) => (d.value < 0
            ? getYForBarChart(d.key as 'deltaInvestors' | 'deltaCommitments', 0)
            : getYForBarChart(d.key as 'deltaInvestors' | 'deltaCommitments', d.value)))
          .attr('height', (d) => Math.abs(
            getYForBarChart(d.key as 'deltaInvestors' | 'deltaCommitments', d.value)
            - getYForBarChart(d.key as 'deltaInvestors' | 'deltaCommitments', 0),
          ))
          .attr('rx', () => (deltas.length > 10 ? 5 : 15))
          .attr('ry', () => (deltas.length > 10 ? 5 : 15))
          .on('mouseover', (d) => {

          })
          .on('mouseout', () => {});
      }
    }
  }, [ref, live]);
  return {
    currentSelection,
    accumulativeCommitments: live.accumulativeCommitments,
    hasTestingTheWatersAccumulative: testingTheWaters.accumulativeCommitments.length > 0,
  };
};

export default useChart;
